/**
 * Small shared pieces of the UI.
 *
 * The visual language: a warm paper canvas, white cards that float on a soft
 * shadow, one accent at a time, and photography carrying the colour instead of
 * flat blocks of it.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Home, MapPin, X } from 'lucide-react'
import { ApiError } from '@/api/client'
import { useCoverage } from '@/api/hooks'
import type { Attraction, Sponsored } from '@/api/types'
import { usePlatform } from '@/auth/AuthProvider'
import { BrandAvatar, BrandMark, CitySkyline, EmptyIllustration, SearchingScene } from '@/illustrations'
import { formatDistance } from '@/lib/geo'
import { t, tList, tPlural } from '@/i18n'

/** Every tap answers back; Telegram gets a real buzz, the browser a no-op. */
export function useHaptic() {
  const platform = usePlatform()
  return (type: 'success' | 'error' | 'light' = 'light') => platform?.haptic(type)
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

export function Screen({
  title,
  subtitle,
  eyebrow,
  children,
  action,
  header,
  chrome,
}: {
  title?: string
  subtitle?: string
  /** The small tracked line above the title, e.g. which of the three modes this is. */
  eyebrow?: string
  children: ReactNode
  /** Sits opposite the back button, in the top row. */
  action?: ReactNode
  /** Replaces the whole top of the screen. */
  header?: ReactNode
  /** Full-bleed content between the top row and the title, e.g. a map. */
  chrome?: ReactNode
}) {
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-screen-sm animate-screen-in flex-col bg-canvas pb-28 sm:max-w-2xl lg:max-w-3xl">
      {header ?? <ScreenHeader title={title} subtitle={subtitle} eyebrow={eyebrow} action={action} />}
      {chrome}
      <div className="flex flex-1 flex-col gap-4 px-5 pt-5">{children}</div>
      <ScreenNav />
    </div>
  )
}

/**
 * The way out of a screen, at the bottom where the thumb is and spelled out in
 * words: by the time the page has been read, a chevron at the top is far away
 * and "back" is no longer obvious.
 */
export function ScreenNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const haptic = useHaptic()

  // Nothing to leave from the home screen, and sign-in has nowhere to go
  if (location.pathname === '/' || location.pathname === '/login') return null

  return (
    <nav className="mt-auto flex flex-wrap gap-2 px-5 pt-10">
      <Button
        variant="secondary"
        onClick={() => {
          if (window.history.length > 1) navigate(-1)
          else navigate('/')
        }}
      >
        <ChevronLeft size={18} strokeWidth={1.9} aria-hidden />
        {t('common.back')}
      </Button>
      <Button
        variant="secondary"
        onClick={() => {
          haptic('light')
          navigate('/')
        }}
      >
        <Home size={18} strokeWidth={1.8} aria-hidden />
        {t('common.home')}
      </Button>
    </nav>
  )
}

/**
 * The wordmark, always in its own casing and in three weights: the middle part
 * is quieter than the two capitalised ends, which is what makes AIroundMe read
 * as one word rather than three.
 */
export function Wordmark({ className = 'text-[15px]' }: { className?: string }) {
  return (
    <span className={`tracking-[-0.02em] text-ink-strong ${className}`}>
      <b className="font-bold">AI</b>
      <span className="font-normal text-ink-soft">round</span>
      <b className="font-bold">Me</b>
    </span>
  )
}

/**
 * The top of a screen, in three bands: the brand row, a hairline, and then the
 * title itself. The title is set in the display serif with a small tracked
 * line above it — a screen that only had a 30 px heading on bare canvas read as
 * an unfinished page rather than a chapter of one app.
 *
 * The way out still lives at the bottom, in words; the wordmark here is a way
 * home, not a back button.
 */
export function ScreenHeader({
  title,
  subtitle,
  eyebrow,
  action,
}: {
  title?: string
  subtitle?: string
  eyebrow?: string
  action?: ReactNode
}) {
  return (
    <div style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
      <div className="flex min-h-touch items-center justify-between gap-3 px-5 py-2">
        <Link to="/" className="flex items-center gap-2.5" aria-label={t('common.home')}>
          <BrandMark className="h-6 w-6" />
          <Wordmark className="text-[14px]" />
        </Link>
        {action}
      </div>
      <div className="h-px bg-hairline" />
      {title && (
        <div className="px-5 pt-5">
          {eyebrow && <p className="text-eyebrow uppercase text-ink-soft">{eyebrow}</p>}
          <h1 className={`font-display text-display text-ink-strong ${eyebrow ? 'mt-2.5' : ''}`}>{title}</h1>
          {subtitle && <p className="mt-2 text-[14px] leading-[21px] text-ink-soft">{subtitle}</p>}
        </div>
      )}
    </div>
  )
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-label uppercase text-ink-faint">{children}</p>
}

/**
 * Способ что-то сделать: номер курсивным серифом, название и одна строка о
 * нём. Номер — единственное место, где на этих экранах появляется акцентный
 * цвет. Общий ряд для главной и для выбора внутри режима.
 */
export function ModeRow({
  to,
  onClick,
  index,
  label,
  hint,
}: {
  to?: string
  onClick?: () => void
  index: string
  label: string
  hint: string
}) {
  const haptic = useHaptic()
  const shell = 'flex w-full min-h-touch items-baseline gap-4 border-b border-hairline py-4 text-left'
  const body = (
    <>
      <span className="w-6 shrink-0 font-display text-[19px] italic leading-none text-clay tnum">{index}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[19px] font-medium tracking-[-0.01em] text-ink-strong">{label}</span>
        <span className="mt-1 block text-[13px] leading-[19px] text-ink-soft">{hint}</span>
      </span>
      <span className="text-ink-faint">
        <ChevronRight size={16} strokeWidth={1.8} aria-hidden />
      </span>
    </>
  )

  if (to) {
    return (
      <Link to={to} onClick={() => haptic('light')} className={shell}>
        {body}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick?.()
      }}
      className={shell}
    >
      {body}
    </button>
  )
}

/**
 * Куда-то посмотреть, а не что-то сделать: без карточки и тени, только
 * волосяная линия сверху и мелкая иконка. Общий ряд для главной и профиля.
 */
export function QuietRow({
  to,
  label,
  hint,
  icon,
  onClick,
}: {
  to?: string
  label: string
  hint?: string
  icon?: ReactNode
  onClick?: () => void
}) {
  const haptic = useHaptic()
  const shell = 'flex w-full min-h-touch items-center gap-3 border-t border-hairline px-1 py-3.5 text-left text-ink-strong'
  const body = (
    <>
      {icon && <span className="flex h-5 w-5 shrink-0 items-center justify-center text-ink-faint">{icon}</span>}
      <span className="min-w-0 flex-1">
        <span className="block text-[15px]">{label}</span>
        {hint && <span className="mt-0.5 block text-[13px] leading-[19px] text-ink-soft">{hint}</span>}
      </span>
      <span className="text-ink-faint">
        <ChevronRight size={15} strokeWidth={1.8} aria-hidden />
      </span>
    </>
  )

  if (to) {
    return (
      <Link to={to} onClick={() => haptic('light')} className={shell}>
        {body}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick?.()
      }}
      className={shell}
    >
      {body}
    </button>
  )
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled,
  full,
  className = '',
  ariaLabel,
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  variant?: 'primary' | 'secondary' | 'sand' | 'ghost'
  size?: 'md' | 'icon'
  disabled?: boolean
  full?: boolean
  className?: string
  ariaLabel?: string
}) {
  const haptic = useHaptic()
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full font-medium transition active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100 disabled:cursor-not-allowed'
  const sizing = size === 'icon' ? 'h-11 w-11' : 'min-h-[52px] px-6 text-[15px]'
  const styles = {
    primary: 'bg-ink-strong text-card',
    secondary: 'border border-hairline bg-card text-ink-strong shadow-card',
    sand: 'bg-sand text-sand-on',
    ghost: 'text-ink-soft',
  }[variant]

  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={
        onClick
          ? () => {
              haptic('light')
              onClick()
            }
          : undefined
      }
      disabled={disabled}
      className={`${base} ${sizing} ${styles} ${full ? 'w-full' : ''} ${className}`}
    >
      {children}
    </button>
  )
}

export function Card({
  children,
  onClick,
  className = '',
  as = 'div',
  to,
  dataId,
  flat,
}: {
  children: ReactNode
  onClick?: () => void
  className?: string
  as?: 'div' | 'article' | 'section'
  to?: string
  /** Lets a screen find this card in the DOM, e.g. to scroll it into view. */
  dataId?: string | number
  /** Sits inside another card: no shadow, no border. */
  flat?: boolean
}) {
  const haptic = useHaptic()
  const shell = `rounded-card text-ink-strong ${
    flat ? 'bg-card-sunk' : 'border border-hairline bg-card shadow-card'
  } ${onClick || to ? 'transition active:scale-[0.985]' : ''} ${className}`

  if (to) {
    return (
      <Link to={to} data-id={dataId} onClick={() => haptic('light')} className={`block ${shell}`}>
        {children}
      </Link>
    )
  }
  if (onClick) {
    return (
      <button
        type="button"
        data-id={dataId}
        onClick={() => {
          haptic('light')
          onClick()
        }}
        className={`text-left ${shell}`}
      >
        {children}
      </button>
    )
  }

  const Tag = as
  return (
    <Tag data-id={dataId} className={shell}>
      {children}
    </Tag>
  )
}

export function Chip({
  children,
  onClick,
  active,
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  active?: boolean
  disabled?: boolean
}) {
  const haptic = useHaptic()
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={
        onClick
          ? () => {
              haptic('light')
              onClick()
            }
          : undefined
      }
      className={`min-h-[40px] rounded-full px-4 text-[14px] transition active:scale-[0.97] disabled:opacity-40 ${
        active
          ? 'bg-ink-strong text-card'
          : 'border border-hairline bg-card text-ink-strong shadow-card'
      }`}
    >
      {children}
    </button>
  )
}

/** Distance over a photo: a glass pill, so it reads on any image. */
export function DistancePill({ meters, onPhoto = true }: { meters: number | null; onPhoto?: boolean }) {
  if (meters === null) return null
  return (
    <span
      className={`tnum inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-medium ${
        onPhoto ? 'bg-black/45 text-white backdrop-blur-sm' : 'text-ink-soft'
      }`}
    >
      <MapPin size={12} strokeWidth={2.2} aria-hidden />
      {formatDistance(meters)}
    </span>
  )
}

/**
 * The rating is not shown as a number: it measures how well documented a place
 * is, and beside a star a 0,3 read as a verdict on the place. Only the places
 * at the top of the scale say so; the rest of the scale is carried by the order.
 */
export function NotableMark({ notable, onPhoto }: { notable: boolean; onPhoto?: boolean }) {
  if (!notable) return null

  // Over a photo it sits on the scrim above the name, not beside the distance
  // pill: a two-column tile on a phone has no room for both up there
  return (
    <span
      className={`whitespace-nowrap font-medium ${
        onPhoto ? 'mb-1 block text-[11px] uppercase tracking-[0.08em] text-white/80' : 'text-[12px] text-ink-strong'
      }`}
    >
      {t('results.notable')}
    </span>
  )
}

export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <span className={`skeleton block rounded-xl ${className}`} aria-hidden />
}

/**
 * What the screen is doing while it waits, in words.
 *
 * The caption walks through the steps and stops on the last one: a wait that
 * keeps naming a new stage forever reads as a wait that is stuck.
 */
export function ProgressNote({ steps, intervalMs = 2200 }: { steps: string[]; intervalMs?: number }) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (steps.length < 2) return
    const timer = setInterval(() => setIndex((current) => Math.min(current + 1, steps.length - 1)), intervalMs)
    return () => clearInterval(timer)
  }, [steps.length, intervalMs])

  if (steps.length === 0) return null

  return (
    // Точки уже есть в самом знаке и в сцене поиска; ещё одна тройка рядом с
    // текстом была третьим способом сказать то же самое
    <p className="px-1 text-[14px] text-ink-soft" role="status" aria-live="polite">
      {steps[index]}
    </p>
  )
}

/** The whole wait: the mascot looking around, and what it is looking for. */
export function SearchingState({ steps }: { steps: string[] }) {
  return (
    <div className="flex flex-col items-center gap-2 py-6">
      <SearchingScene className="h-28 w-32" />
      <ProgressNote steps={steps} />
    </div>
  )
}

export function ResultsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-label={t('common.loading')}>
      <Skeleton className="col-span-2 aspect-[4/3] rounded-card sm:aspect-video" />
      <Skeleton className="h-36 rounded-card" />
      <Skeleton className="h-36 rounded-card" />
      <Skeleton className="col-span-2 h-[76px] rounded-card sm:col-span-3" />
    </div>
  )
}

export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3" aria-label={t('common.loading')}>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-[76px] rounded-card" />
      ))}
    </div>
  )
}

export function BotAvatar({ className = '' }: { className?: string }) {
  return (
    // The avatar variant of the mark, not the mascot: the mascot never stands
    // in for the logo in headers, avatars or the favicon.
    <BrandAvatar className={`h-8 w-8 shrink-0 ${className}`} />
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Card className="p-5">
      <p className="text-[15px] text-ink-strong">{describeError(error)}</p>
      {onRetry && (
        <div className="mt-4">
          <Button variant="secondary" onClick={onRetry}>
            {t('errors.retry')}
          </Button>
        </div>
      )}
    </Card>
  )
}

export function EmptyState({
  text,
  children,
  wide,
}: {
  text: string
  children?: ReactNode
  /** A long line that should keep the full width instead of wrapping early. */
  wide?: boolean
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <EmptyIllustration className="h-24 w-28" />
      <p className={`text-[15px] text-ink-soft ${wide ? 'w-full' : 'max-w-[18rem]'}`}>{text}</p>
      {children}
    </div>
  )
}

/**
 * A way out of an empty search: the border of the base is a fact about the
 * base, not a failure of the search, and the list says where it does reach.
 * Silent while the numbers are loading — an empty state is no place to wait.
 */
export function CoverageHint() {
  const { data } = useCoverage()
  if (!data || data.countries_count === 0) return null

  return (
    <div className="flex w-full flex-col gap-1.5 text-[13px] leading-[18px] text-ink-faint">
      <p>
        {t('coverage.empty_hint', {
          countries: data.countries_count,
          countries_word: tPlural('coverage.countries_word', data.countries_count),
        })}
      </p>
      {/* On its own line: it is the thing to do next, not a tail of the sentence */}
      <Link to="/coverage" className="text-ink-strong underline decoration-hairline-strong underline-offset-4">
        {t('coverage.link')}
      </Link>
    </div>
  )
}

export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'llm_unavailable':
        return t('errors.llm')
      case 'rate_limited':
        return t('errors.rate_limited')
      case 'identity_taken':
        return t('errors.identity_taken')
      case 'merge_ticket_invalid':
        return t('errors.merge_ticket_invalid')
      case 'telegram_link_failed':
        return t('errors.telegram_link_failed')
      case 'payment_failed':
        return t('errors.payment_failed')
      case 'email_code_rejected':
        return t('errors.email_code_rejected')
      case 'unauthorized':
        return t('errors.unauthorized')
      case 'invalid_input':
      case 'not_found':
        return error.message
      default:
        return t('errors.general')
    }
  }
  if (error instanceof TypeError) return t('errors.network')
  return t('errors.general')
}

/**
 * The answer takes 10-20 seconds. A skeleton bubble with the mascot reads as
 * "it is writing" rather than "it is stuck"; the caption underneath changes
 * slowly so the eye is not pulled away from the page.
 */
export function ThinkingBubble() {
  const steps = tList('dialog.loading_steps')
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setIndex((value) => (value + 1) % Math.max(steps.length, 1)), 5000)
    return () => clearInterval(timer)
  }, [steps.length])

  return (
    <div className="flex items-start gap-3" role="status" aria-live="polite">
      <BotAvatar className="mt-0.5" />
      {/* Ожидание показывают строки будущего ответа, а не ещё одна тройка
          точек рядом со знаком, который сам из точек */}
      <div className="flex min-w-0 max-w-[22rem] flex-1 flex-col gap-2.5 py-1">
        <Skeleton className="h-2.5 w-full" />
        <Skeleton className="h-2.5 w-3/4" />
        <span className="text-[13px] text-ink-faint">{steps[index] ?? t('dialog.loading')}</span>
      </div>
    </div>
  )
}

/** Reveals an answer that already arrived; the API returns it in one piece. */
export function useTypewriter(text: string, enabled = true): string {
  const [shown, setShown] = useState(() => (enabled ? '' : text))
  const reduced = useMemo(prefersReducedMotion, [])

  useEffect(() => {
    if (!enabled || reduced || !text) {
      setShown(text)
      return
    }

    setShown('')
    const totalMs = 900
    const step = Math.max(1, Math.ceil(text.length / (totalMs / 16)))
    let cursor = 0
    const timer = setInterval(() => {
      cursor += step
      setShown(text.slice(0, cursor))
      if (cursor >= text.length) clearInterval(timer)
    }, 16)

    return () => clearInterval(timer)
  }, [text, enabled, reduced])

  return shown
}

export function Paywall({ kind, limit, onClose }: { kind: 'daily' | 'follow_up'; limit?: number; onClose?: () => void }) {
  const isDaily = kind === 'daily'
  return (
    <section className="overflow-hidden rounded-card border border-hairline bg-card shadow-card">
      <div className="h-1 w-full bg-sand" />
      <div className="p-5">
        <h2 className="text-title text-ink-strong">
          {isDaily ? t('paywall.daily_title') : t('paywall.follow_up_title')}
        </h2>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-soft">
          {isDaily ? t('paywall.daily_text', { limit: limit ?? 3 }) : t('paywall.follow_up_text')}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Link
            to="/tariffs"
            className="inline-flex min-h-[52px] items-center rounded-full bg-ink-strong px-6 text-[15px] font-medium text-card transition active:scale-[0.98]"
          >
            {t('paywall.subscribe')}
          </Link>
          {onClose && (
            <Button variant="ghost" onClick={onClose}>
              {t('paywall.later')}
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}

export type TileSize = 'hero' | 'large' | 'row'

/**
 * The mosaic: results arrive sorted by rating, so position carries most of the
 * signal. A place far below the leader is demoted to a row even if it landed
 * high in the list.
 */
export interface MosaicSlot {
  /** How many grid columns this card takes. */
  span: number
  kind: TileSize
  /** Which row of the grid this card lands in. */
  row: number
}

export type Orientation = 'portrait' | 'landscape' | 'unknown'

/** The API carries the pixel size, so this is known before anything is drawn. */
export function orientationOf(attraction: Attraction): Orientation {
  const { image_width: width, image_height: height } = attraction
  if (!attraction.image_url || !width || !height) return 'unknown'
  if (height > width * 1.1) return 'portrait'
  if (width > height * 1.1) return 'landscape'
  return 'landscape'
}

/**
 * Every row is filled. The leader gets the full width, the rest are laid in
 * complete rows, and whatever does not divide evenly becomes either a wide
 * pair or a single full-width strip — never a tile with a hole beside it.
 */
export function mosaicLayout(count: number, columns: number): MosaicSlot[] {
  if (count <= 0) return []

  let row = 0
  const slots: MosaicSlot[] = [{ span: columns, kind: 'hero', row }]
  let rest = count - 1

  while (rest >= columns) {
    row += 1
    for (let index = 0; index < columns; index += 1) slots.push({ span: 1, kind: 'large', row })
    rest -= columns
  }

  if (rest === 1) {
    row += 1
    slots.push({ span: columns, kind: 'row', row })
  } else if (rest > 1) {
    // Only reachable with three columns: two cards share the row as 2 + 1.
    row += 1
    slots.push({ span: columns - 1, kind: 'large', row })
    slots.push({ span: 1, kind: 'large', row })
  }

  return slots
}

/** Two columns on a phone, three from the small breakpoint up. */
function useColumns(): number {
  const [columns, setColumns] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(min-width: 640px)').matches ? 3 : 2,
  )

  useEffect(() => {
    const query = window.matchMedia?.('(min-width: 640px)')
    if (!query) return
    const apply = () => setColumns(query.matches ? 3 : 2)
    apply()
    query.addEventListener('change', apply)
    return () => query.removeEventListener('change', apply)
  }, [])

  return columns
}

/** The same "no image" artwork the bot sends, so both clients look alike. */
const NO_IMAGE = '/no-image.png'

/**
 * Portrait photographs of landmarks put the interesting part — a spire, a
 * dome, a facade — above the middle, so a centred crop beheads them. The
 * orientation is only known once the image has loaded, so it moves the crop
 * focus and nothing else: changing object-position reflows no layout, while
 * moving the tile itself would make the whole grid jump under the reader.
 */
function cropFocus(image: HTMLImageElement): 'tall' | 'wide' {
  return image.naturalHeight > image.naturalWidth * 1.1 ? 'tall' : 'wide'
}

function Photo({ attraction, className }: { attraction: Attraction; className: string }) {
  const [failed, setFailed] = useState(false)
  // The API usually knows the size, so the crop is right from the first paint;
  // onLoad only fills in for rows the backfill has not reached yet.
  const [focus, setFocus] = useState<'tall' | 'wide'>(
    orientationOf(attraction) === 'portrait' ? 'tall' : 'wide',
  )
  const missing = !attraction.image_url || failed

  // The placeholder is square with lettering in it: cropping would cut the
  // words in half, so it is fitted whole against the sunken surface.
  const fit = missing
    ? 'bg-card-sunk object-contain p-2'
    : `object-cover ${focus === 'tall' ? 'object-[50%_28%]' : 'object-center'}`

  return (
    <img
      src={missing ? NO_IMAGE : attraction.image_url!}
      alt=""
      loading="lazy"
      decoding="async"
      className={`${className} block ${fit}`}
      onLoad={(event) => {
        if (!missing) setFocus(cropFocus(event.currentTarget))
      }}
      onError={() => setFailed(true)}
    />
  )
}

export function AttractionTile({
  attraction,
  size,
  onOpen,
  highlighted,
  index = 0,
  largeThumb,
  badge,
}: {
  attraction: Attraction
  size: TileSize
  onOpen: () => void
  highlighted?: boolean
  index?: number
  /** A row whose photo is what the reader compares, as in photo search. */
  largeThumb?: boolean
  /** A short mark under the name, e.g. that the AI's answer is this place. */
  badge?: string
}) {
  const ring = highlighted ? 'ring-2 ring-ember ring-offset-2 ring-offset-canvas' : ''
  const enter = { animationDelay: `${Math.min(index, 8) * 40}ms` }

  if (size === 'row' && largeThumb) {
    return (
      <Card
        onClick={onOpen}
        dataId={attraction.id}
        className={`flex h-full w-full animate-rise-in items-center gap-3.5 p-3 ${ring}`}
      >
        <Photo
          attraction={attraction}
          className="h-[84px] w-28 shrink-0 overflow-hidden rounded-xl sm:h-[120px] sm:w-40"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-1" style={enter}>
          {/* The larger photo leaves the text a narrow column on a phone */}
          <span className="line-clamp-3 text-[15px] font-medium leading-5 sm:line-clamp-2">{attraction.name}</span>
          {attraction.address && (
            <span className="line-clamp-2 text-[13px] leading-[18px] text-ink-soft">{attraction.address}</span>
          )}
          {badge && (
            <span className="flex items-center gap-1 text-[12px] font-medium text-ember">
              <Check size={13} strokeWidth={2.2} aria-hidden />
              {badge}
            </span>
          )}
          <NotableMark notable={attraction.notable} />
        </span>
        <ChevronRight size={16} strokeWidth={1.8} className="shrink-0 text-ink-faint" aria-hidden />
      </Card>
    )
  }

  if (size === 'row') {
    return (
      <Card
        onClick={onOpen}
        dataId={attraction.id}
        className={`flex h-full w-full animate-rise-in items-center gap-3.5 p-3 ${ring}`}
      >
        <Photo attraction={attraction} className="h-[52px] w-[52px] shrink-0 overflow-hidden rounded-xl" />
        <span className="min-w-0 flex-1" style={enter}>
          <span className="line-clamp-2 block text-[15px] font-medium leading-5">{attraction.name}</span>
          <span className="mt-1 flex items-center gap-2 text-[13px] text-ink-soft">
            {/* Photo search has no position to measure from, so the address stands in */}
            <span className="tnum truncate">
              {attraction.distance_m !== null ? formatDistance(attraction.distance_m) : attraction.address}
            </span>
            <NotableMark notable={attraction.notable} />
          </span>
        </span>
        <ChevronRight size={16} strokeWidth={1.8} className="shrink-0 text-ink-faint" aria-hidden />
      </Card>
    )
  }

  const isHero = size === 'hero'

  // The photo fills the card absolutely, so every tile in a row is exactly the
  // same height and no white gap can open up under a shorter image.
  return (
    <Card
      onClick={onOpen}
      dataId={attraction.id}
      className={`scrim relative h-full w-full animate-rise-in overflow-hidden p-0 ${ring}`}
    >
      <span className="block h-full" style={enter}>
        <Photo attraction={attraction} className="absolute inset-0 h-full w-full" />
        <span className="absolute left-3 top-3 z-10 flex items-center gap-1.5">
          <DistancePill meters={attraction.distance_m} />
        </span>
        <span className="absolute inset-x-0 bottom-0 z-10 block p-3.5">
          <NotableMark notable={attraction.notable} onPhoto />
          <span
            className={`block font-semibold text-white ${isHero ? 'text-[19px] leading-6' : 'line-clamp-2 text-[15px] leading-5'}`}
          >
            {attraction.name}
          </span>
          {isHero && attraction.address && (
            <span className="mt-1 block truncate text-[13px] text-white/75">{attraction.address}</span>
          )}
        </span>
      </span>
    </Card>
  )
}

/**
 * Row heights, by what is actually in the row. A row of upright photographs
 * gets the height they need instead of being cropped to a letterbox, and a row
 * of wide ones stays compact — while the row itself is still full, so no hole
 * can open up and nothing moves after the images load.
 */
const ROW_HEIGHT = { landscape: '10rem', portrait: '14rem' }

/**
 * The leader keeps a photographic shape instead of a fixed height, which on a
 * wide screen stretched it into a letterbox: 4:3 on a phone, where upright
 * landmarks need the height, 16:9 from two columns up, where 4:3 would fill the
 * whole screen and push the other places out of sight.
 */
export const HERO_ASPECT = 'aspect-[4/3] sm:aspect-video'

function rowHeight(kind: TileSize, members: Attraction[]): string | undefined {
  if (kind !== 'large') return undefined

  const known = members.map(orientationOf).filter((value) => value !== 'unknown')
  const portrait = known.filter((value) => value === 'portrait').length
  // Unknown sizes keep the compact height: that is what the app looked like
  // before the dimensions existed, and it is the safer default.
  const tall = known.length > 0 && portrait * 2 >= known.length

  return tall ? ROW_HEIGHT.portrait : ROW_HEIGHT.landscape
}

export function AttractionMosaic({
  attractions,
  onOpen,
  highlightedId,
  sponsored,
  onSponsoredOpen,
}: {
  attractions: Attraction[]
  onOpen: (id: number) => void
  highlightedId?: number | null
  sponsored?: Sponsored | null
  onSponsoredOpen?: (url: string) => void
}) {
  const columns = useColumns()
  const slots = mosaicLayout(attractions.length, columns)

  // Everything sharing a row shares its height, so the row cannot end ragged.
  const heights = new Map<number, string | undefined>()
  for (const slot of slots) {
    if (heights.has(slot.row)) continue
    const members = attractions.filter((_, index) => slots[index]?.row === slot.row)
    heights.set(slot.row, rowHeight(slot.kind, members))
  }

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {attractions.map((attraction, index) => {
        const slot = slots[index] ?? { span: 1, kind: 'large' as TileSize, row: -1 }
        return (
          <div
            key={attraction.id}
            className={slot.kind === 'hero' ? HERO_ASPECT : undefined}
            style={{ gridColumn: `span ${slot.span}`, height: heights.get(slot.row) }}
          >
            <AttractionTile
              attraction={attraction}
              size={slot.kind}
              index={index}
              highlighted={highlightedId === attraction.id}
              onOpen={() => onOpen(attraction.id)}
            />
          </div>
        )
      })}
      {sponsored && onSponsoredOpen && (
        <div style={{ gridColumn: `span ${columns}` }}>
          <SponsoredCard card={sponsored} onOpen={onSponsoredOpen} />
        </div>
      )}
    </div>
  )
}

export function SponsoredCard({ card, onOpen }: { card: Sponsored; onOpen: (url: string) => void }) {
  return (
    <Card as="article" className="overflow-hidden">
      <img src={card.image_url} alt="" loading="lazy" className="h-36 w-full object-cover" />
      <div className="flex flex-col gap-2 p-5">
        {/* The label is a label; the advertiser's legal name is a proper noun
            and keeps the casing it was registered with. */}
        <p className="text-[12px] text-ink-faint">
          <span className="uppercase tracking-[0.04em]">{t('results.ad')}</span> · {card.legal_entity_name}
        </p>
        <h3 className="text-[17px] font-semibold">{card.title}</h3>
        <p className="text-[14px] text-ink-soft">{card.short_text}</p>
        {card.offer_text && <p className="text-[14px]">{card.offer_text}</p>}
        <div className="pt-2">
          <Button variant="secondary" onClick={() => onOpen(card.target_url)} full>
            {card.button_text}
          </Button>
        </div>
        <p className="text-[11px] text-ink-faint">{t('results.ad_erid', { erid: card.erid })}</p>
      </div>
    </Card>
  )
}

/** A single result outside a mosaic. */
export function AttractionCard({ attraction, onOpen }: { attraction: Attraction; onOpen: () => void }) {
  return <AttractionTile attraction={attraction} size="hero" onOpen={onOpen} />
}

export function FollowUpChips({
  questions,
  onPick,
  disabled,
}: {
  questions: string[]
  onPick: (index: number) => void
  disabled?: boolean
}) {
  if (!questions.length) return null

  return (
    <div className="flex flex-wrap gap-2">
      {questions.map((question, index) => (
        <Chip key={`${index}-${question}`} onClick={() => onPick(index)} disabled={disabled}>
          {question}
        </Chip>
      ))}
    </div>
  )
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 text-ink-soft" role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
      <span className="text-[15px]">{label ?? t('common.loading')}</span>
    </div>
  )
}


/**
 * The same allowance, set as a figure rather than as a sentence: the number is
 * the biggest thing in the card, the bars under it show the same count a second
 * time, and the way to lift the limit is a quiet link in the corner instead of
 * a second row with its own icon.
 *
 * Used on the home screen, where the number is a fact to glance at; the fuller
 * card above still serves the profile, where it is the subject of the page.
 */
export function AllowanceFigure({
  used,
  limit,
  left,
  premium,
  admin = false,
  until,
  upgradeLink = true,
}: {
  used: number
  limit: number
  left: number | null
  premium: boolean
  /** Админ без подписки тоже без лимита, но премиумом его не называем */
  admin?: boolean
  /** Когда премиум заканчивается: ISO-дата из профиля */
  until?: string | null
  /** Тихая ссылка на тарифы в углу: не нужна там, где рядом стоит кнопка */
  upgradeLink?: boolean
}) {
  const unlimited = limit < 0 || premium

  /* Премиум носит свою полосу сверху — та же метка, что и в профиле, — а под
     ней говорит три вещи по порядку: какой тариф, до какого числа и что
     запросы не ограничены. Безлимит без подписки бывает только у админа:
     та же карточка, но без полосы и с честным названием */
  if (unlimited) {
    const plan = premium ? 'profile.plan_premium' : admin ? 'profile.plan_admin' : 'allowance.plan_free'
    return (
      <Card className="overflow-hidden">
        {premium && <div className="h-1 w-full bg-sand" />}
        <div className="p-[18px]">
          <p className="text-eyebrow uppercase text-ink-soft">{t('home.today')}</p>
          <p className="mt-2.5 font-display text-[22px] leading-[28px] text-ink-strong">{t(plan)}</p>
          {until && (
            <p className="mt-1.5 text-[13px] text-ink-soft">
              {t('profile.until', { date: new Date(until).toLocaleDateString('ru-RU') })}
            </p>
          )}
          <p className="mt-2.5 border-t border-hairline pt-2.5 text-[13px] text-ink-soft">
            {t('allowance.unlimited')}
          </p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-[18px]">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-eyebrow uppercase text-ink-soft">{t('allowance.plan_free')}</p>
        {upgradeLink && (
          <Link
            to="/tariffs"
            className="text-[12px] text-ink-soft underline decoration-hairline-strong underline-offset-4"
          >
            {t('allowance.upgrade_hint')}
          </Link>
        )}
      </div>

      <AllowanceCount used={used} limit={limit} left={left} />
    </Card>
  )
}

function AllowanceCount({ used, limit, left }: { used: number; limit: number; left: number | null }) {
  const remaining = left ?? Math.max(limit - used, 0)
  const spent = remaining === 0

  return (
    <>
      <div className="mt-3 flex items-baseline gap-2.5">
        <span className={`font-display text-[38px] leading-[38px] tnum ${spent ? 'text-ember' : 'text-ink-strong'}`}>
          {remaining}
        </span>
        <span className="text-[14px] leading-[19px] text-ink-soft">
          {spent
            ? t('allowance.spent')
            : tPlural('allowance.left_until_tomorrow', remaining)}
        </span>
      </div>

      {/* The bars say the same thing as the number; they are there for the
          glance that does not stop to read it. */}
      <div className="mt-3.5 flex gap-1.5" aria-hidden>
        {Array.from({ length: Math.max(limit, 0) }).map((_, index) => (
          <span
            key={index}
            className={`h-[3px] flex-1 transition-colors duration-300 ${
              index < remaining ? 'bg-ink-strong' : 'bg-hairline-strong'
            }`}
          />
        ))}
      </div>

      <p className="mt-2.5 text-[12px] text-ink-soft">
        {spent ? (
          <Link to="/tariffs" className="text-ink-strong underline decoration-hairline-strong underline-offset-4">
            {t('allowance.upgrade_now')}
          </Link>
        ) : (
          t('allowance.renews')
        )}
      </p>
    </>
  )
}

/**
 * What a mode does, in the order it does it: the steps, then the fine print.
 *
 * It opens by itself while the screen is still empty and folds away once
 * results are on the page, so the instruction never competes with them.
 */
export function HowItWorks({
  steps,
  tips,
  good,
  bad,
  open,
  onToggle,
}: {
  steps: string[]
  tips?: string[]
  /** A worked example for photo-like modes: what to send, what not to. */
  good?: { title: string; items: string[] }
  bad?: { title: string; items: string[] }
  open: boolean
  onToggle: () => void
}) {
  const haptic = useHaptic()

  return (
    <section className="overflow-hidden rounded-card border border-hairline bg-card shadow-card">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          haptic('light')
          onToggle()
        }}
        className="flex min-h-touch w-full items-center gap-3 px-4 py-3 text-left transition active:bg-card-sunk"
      >
        <span className="min-w-0 flex-1 text-[15px] font-medium text-ink-strong">{t('howto.title')}</span>
        <ChevronDown
          size={18}
          strokeWidth={1.8}
          className={`shrink-0 text-ink-faint transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="border-t border-hairline px-4 pb-4 pt-4">
          <ol className="flex flex-col gap-3.5">
            {steps.map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="tnum mt-[1px] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-card-sunk text-[12px] font-medium text-ink-strong">
                  {index + 1}
                </span>
                <span className="min-w-0 text-[14px] leading-[21px] text-ink-soft">{step}</span>
              </li>
            ))}
          </ol>

          {(good || bad) && (
            <div className="mt-4 grid gap-3 border-t border-hairline pt-4 sm:grid-cols-2">
              {good && <ExampleList title={good.title} items={good.items} ok />}
              {bad && <ExampleList title={bad.title} items={bad.items} />}
            </div>
          )}

          {tips && tips.length > 0 && (
            <ul className="mt-4 flex flex-col gap-2 border-t border-hairline pt-4">
              {tips.map((tip, index) => (
                <li key={index} className="text-[13px] leading-[19px] text-ink-faint">
                  {tip}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

function ExampleList({ title, items, ok }: { title: string; items: string[]; ok?: boolean }) {
  const Icon = ok ? Check : X
  return (
    <div className="flex flex-col gap-2">
      <p className="text-label uppercase text-ink-faint">{title}</p>
      <ul className="flex flex-col gap-1.5">
        {items.map((item, index) => (
          <li key={index} className="flex gap-2 text-[13px] leading-[19px] text-ink-soft">
            <Icon
              size={14}
              strokeWidth={2}
              className={`mt-[3px] shrink-0 ${ok ? 'text-ink-strong' : 'text-ink-faint'}`}
              aria-hidden
            />
            <span className="min-w-0">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export { CitySkyline }
